import React, { useState, useRef } from 'react';
import { 
  BarChart3, Shield, Users, Zap, ArrowRight, Check, 
  TrendingUp, Globe, Server, ChevronRight, Play, LineChart,
  Wallet, PieChart, Lock, Bell, Layers, Target, Clock,
  CreditCard, Building, Mail, Phone, MapPin, Send,
  Twitter, Linkedin, Github, ArrowUpRight, Sparkles,
  Monitor, Database, Activity, Settings, ChevronDown, Download
} from 'lucide-react';
import { toast } from 'sonner';
import { AstraMobileMenuButton, AstraMobileMenu } from '../components/AstraMobileMenu';
import FloatingChatButtons from '../components/FloatingChatButtons';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Deep Dark Design - Darker blue, no borders
const THEME = {
  bg: '#050507',              // Deep black
  bgCard: '#0c0c10',          // Subtle card bg
  bgElevated: '#111116',      // Elevated surface
  bgHover: '#16161d',         // Hover state
  accent: '#072e4f',          // Dark navy blue
  accentDark: '#051d33',      // Darker navy
  accentGlow: 'rgba(7, 46, 79, 0.15)',
  text: '#f8fafc',
  textSecondary: '#a1a1aa',   // Zinc gray
  textMuted: '#52525b',       // Muted zinc
  textDim: '#3f3f46',         // Dim zinc
  success: '#22c55e',
};

// Features data
const FEATURES = [
  {
    icon: LineChart,
    title: 'Trading Terminal',
    desc: 'Professional charting with 50+ technical indicators and sub-10ms execution.',
  },
  {
    icon: Shield,
    title: 'Admin Dashboard',
    desc: 'Complete back-office for client management, KYC, and risk monitoring.',
  },
  {
    icon: Users,
    title: 'Client Portal',
    desc: 'White-labeled portal with registration and integrated payments.',
  },
  {
    icon: Wallet,
    title: 'Crypto Payments',
    desc: 'Traders fund accounts in USDT/USDC on Tron, Ethereum, BSC and Polygon — credited automatically on-chain.',
  },
  {
    icon: Target,
    title: 'IB Management',
    desc: 'Multi-tier affiliate system with automated commission payouts.',
  },
  {
    icon: Activity,
    title: 'Social Trading',
    desc: 'Copy trading with performance analytics and trader leaderboards.',
  },
];

// Pricing tiers
const PRICING = [
  {
    name: 'Starter',
    price: '980',
    desc: 'For new brokerages',
    features: ['500 active clients', 'Trading terminal', 'Client portal', 'Basic admin', 'Integrated crypto deposits (USDT/USDC)', 'Social trading platform', 'CRM', 'Landing page builder', 'Risk management tools', 'A Book / B Book', 'Trader groups', 'Email support'],
    popular: false,
  },
  {
    name: 'Professional',
    price: '2,700',
    desc: 'For growing brokers',
    features: ['2,500 active clients', 'Everything in Starter', 'IB management', 'Payment gateways', 'Priority support', 'API access', 'Advanced analytics'],
    popular: true,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    desc: 'For large operations',
    features: ['Unlimited clients', 'Everything in Pro', 'Mobile app', 'Dedicated manager', 'Custom integrations', 'White-glove onboarding'],
    popular: false,
  },
];

// Stats removed

export default function AstraLandingPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    plan: 'professional'
  });
  const [submitting, setSubmitting] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const inquiryRef = useRef(null);

  // Scroll listener for header transformation
  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToInquiry = () => {
    inquiryRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/inquiries/broker-setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contact_name: formData.name,
          email: formData.email,
          company_name: formData.company,
          phone: formData.phone,
          requirements: formData.message,
          selected_plan: formData.plan,
          expected_clients: 'not_specified'
        })
      });
      
      if (response.ok) {
        toast.success('Thank you. We\'ll contact you within 24 hours.');
        setFormData({ name: '', email: '', company: '', phone: '', message: '', plan: 'professional' });
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: THEME.bg, fontFamily: "'Inter', -apple-system, sans-serif" }}>
      
      {/* ===== HEADER ===== */}
      <header 
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        style={{ backgroundColor: 'transparent' }}
      >
        <div className="max-w-6xl mx-auto px-6">
          <div className={`flex items-center transition-all duration-500 ${scrolled ? 'justify-start pt-4' : 'justify-between h-16'}`}>
            {/* All nav items collapse into a box on scroll */}
            <div 
              className={`flex items-center transition-all duration-500 ${
                scrolled 
                  ? 'gap-5 px-4 py-2.5 rounded-full' 
                  : 'gap-2'
              }`}
              style={{ 
                backgroundColor: scrolled ? `${THEME.bgCard}ee` : 'transparent',
                backdropFilter: scrolled ? 'blur(12px)' : 'none',
              }}
            >
              {/* Logo */}
              <div className="flex items-center gap-2">
                <img 
                  src="/assets/astra-logo-full.png" 
                  alt="AstraTerminal" 
                  className={`object-contain transition-all duration-500 ${scrolled ? 'h-8' : 'h-10'}`}
                  style={{ width: 'auto' }}
                />
              </div>
              
              {/* Nav items - inside box on scroll (desktop only) */}
              {scrolled && (
                <div className="hidden md:flex items-center gap-5">
                  <div className="w-px h-4" style={{ backgroundColor: THEME.textDim }} />
                  <nav className="flex items-center gap-4">
                    {['Product', 'Pricing', 'Widgets & API', 'Features'].map((item) => (
                      <a
                        key={item}
                        href={item === 'Features' ? '/features' : item === 'Widgets & API' ? '/widgets-api' : `#${item.toLowerCase()}`}
                        className="text-xs transition-colors"
                        style={{ color: THEME.textSecondary }}
                        onMouseEnter={(e) => e.target.style.color = THEME.text}
                        onMouseLeave={(e) => e.target.style.color = THEME.textSecondary}
                      >
                        {item}
                      </a>
                    ))}
                  </nav>
                  <div className="w-px h-4" style={{ backgroundColor: THEME.textDim }} />
                  <a
                    href="/trading"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs"
                    style={{ color: THEME.textMuted }}
                  >
                    Live Chart
                  </a>
                  <a
                    href="/login"
                    className="text-xs font-medium"
                    style={{ color: THEME.textSecondary }}
                    data-testid="header-login-scrolled"
                    onMouseEnter={(e) => e.target.style.color = THEME.text}
                    onMouseLeave={(e) => e.target.style.color = THEME.textSecondary}
                  >
                    Sign in
                  </a>
                  <button
                    onClick={scrollToInquiry}
                    className="text-xs font-medium px-3 py-1.5 rounded-full"
                    style={{ backgroundColor: '#d4d4d8', color: THEME.bg }}
                  >
                    Get Started
                  </button>
                </div>
              )}
            </div>
            
            {/* Normal header layout when not scrolled */}
            {!scrolled && (
              <>
                <nav className="hidden md:flex items-center gap-8">
                  {['Product', 'Pricing', 'Widgets & API', 'Features'].map((item) => (
                    <a
                      key={item}
                      href={item === 'Features' ? '/features' : item === 'Widgets & API' ? '/widgets-api' : `#${item.toLowerCase()}`}
                      className="text-sm transition-colors"
                      style={{ color: THEME.textSecondary }}
                      onMouseEnter={(e) => e.target.style.color = THEME.text}
                      onMouseLeave={(e) => e.target.style.color = THEME.textSecondary}
                    >
                      {item}
                    </a>
                  ))}
                </nav>
                
                <div className="hidden md:flex items-center gap-3">
                  <a
                    href="/trading"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm px-4 py-2"
                    style={{ color: THEME.textSecondary }}
                    onMouseEnter={(e) => e.target.style.color = THEME.text}
                    onMouseLeave={(e) => e.target.style.color = THEME.textSecondary}
                  >
                    Live Chart
                  </a>
                  <a
                    href="/login"
                    className="text-sm font-medium px-3 py-2"
                    style={{ color: THEME.textSecondary }}
                    data-testid="header-login"
                    onMouseEnter={(e) => e.target.style.color = THEME.text}
                    onMouseLeave={(e) => e.target.style.color = THEME.textSecondary}
                  >
                    Sign in
                  </a>
                  <button
                    onClick={scrollToInquiry}
                    className="text-sm font-medium px-4 py-2 rounded-full"
                    style={{ backgroundColor: '#d4d4d8', color: THEME.bg }}
                  >
                    Get Started
                  </button>
                </div>
              </>
            )}

            {/* Mobile menu button — always shown on mobile */}
            <div className="md:hidden ml-auto">
              <AstraMobileMenuButton open={mobileOpen} onClick={() => setMobileOpen((v) => !v)} theme={THEME} />
            </div>
          </div>
        </div>
      </header>

      <AstraMobileMenu
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        theme={THEME}
        ctaLabel="Get Started"
        ctaHref="/register"
        signInHref="/login"
      />

      {/* ===== HERO WITH BLACK HOLE ===== */}
      <section className="relative min-h-screen overflow-hidden">
        {/* Black hole image background */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1768824264646-ea641cc71f5e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxibGFjayUyMGhvbGUlMjBzcGFjZSUyMGFzdHJvbm9teXxlbnwwfHx8fDE3NzU1MTgzNjN8MA&ixlib=rb-4.1.0&q=85"
            alt="Black hole"
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 30%' }}
          />
          {/* Gradient overlays for text readability */}
          <div 
            className="absolute inset-0"
            style={{ 
              background: `linear-gradient(to right, ${THEME.bg} 0%, ${THEME.bg}ee 30%, transparent 60%)` 
            }}
          />
          <div 
            className="absolute inset-0"
            style={{ 
              background: `linear-gradient(to top, ${THEME.bg} 0%, transparent 40%)` 
            }}
          />
        </div>
        
        {/* Content - positioned left bottom */}
        <div className="relative max-w-6xl mx-auto px-6 min-h-screen flex items-end pb-24">
          <div className="max-w-xl">
            {/* Headline */}
            <h1 
              className="text-5xl md:text-6xl font-semibold leading-tight mb-6 tracking-tight"
              style={{ color: THEME.text }}
            >
              Launch your brokerage
              <br />
              <span style={{ color: THEME.textSecondary }}>in days, not months</span>
            </h1>
            
            {/* Subheadline */}
            <p 
              className="text-lg mb-8"
              style={{ color: THEME.textMuted }}
            >
              Complete trading platform with terminal, admin dashboard, 
              and client portal — deployed under your brand.
            </p>
            
            {/* CTAs */}
            <div className="flex items-center gap-3">
              <button
                onClick={scrollToInquiry}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium"
                style={{ backgroundColor: '#d4d4d8', color: THEME.bg }}
              >
                Start free trial
                <ArrowRight className="h-4 w-4" />
              </button>
              
              <a
                href="/trading"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm"
                style={{ backgroundColor: `${THEME.bgCard}cc`, color: THEME.textSecondary, backdropFilter: 'blur(8px)' }}
              >
                <Play className="h-4 w-4" />
                Live Chart
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section id="product" className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm font-medium mb-3" style={{ color: THEME.accent }}>Platform</p>
            <h2 className="text-3xl md:text-4xl font-semibold mb-4" style={{ color: THEME.text }}>
              Complete Infrastructure
            </h2>
            <p className="text-base max-w-lg mx-auto" style={{ color: THEME.textMuted }}>
              Everything required to launch and operate a professional brokerage.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, idx) => (
              <div
                key={idx}
                className="p-6 rounded-2xl transition-colors"
                style={{ backgroundColor: THEME.bgCard }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = THEME.bgElevated}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = THEME.bgCard}
              >
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-5"
                  style={{ backgroundColor: THEME.bgElevated }}
                >
                  <feature.icon className="h-5 w-5" style={{ color: THEME.accent }} />
                </div>
                
                <h3 className="text-base font-medium mb-2" style={{ color: THEME.text }}>
                  {feature.title}
                </h3>
                
                <p className="text-sm leading-relaxed" style={{ color: THEME.textMuted }}>
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== MULTI-DEVICE AVAILABILITY ===== */}
      <section className="py-32 overflow-hidden relative" style={{ backgroundColor: THEME.bg }}>
        {/* Ambient background glow */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div 
            className="w-[800px] h-[500px] rounded-full opacity-[0.07]"
            style={{ 
              background: 'radial-gradient(ellipse, #1a5c96 0%, transparent 70%)',
              filter: 'blur(80px)'
            }}
          />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-20">
            <p className="text-sm font-medium tracking-widest uppercase mb-4" style={{ color: '#1a6aaa' }}>Availability</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold mb-5" style={{ color: THEME.text }}>
              Trade anywhere, anytime
            </h2>
            <p className="text-base max-w-md mx-auto" style={{ color: THEME.textMuted }}>
              Seamless experience across every device and platform.
            </p>
          </div>

          {/* Device Showcase - Premium Layout */}
          <div className="relative flex items-end justify-center" style={{ height: '520px' }} data-testid="device-showcase">
            
            {/* === LEFT: iPad Tablet === */}
            <div 
              className="absolute hidden lg:block"
              style={{ 
                left: '2%', 
                bottom: '40px',
                zIndex: 2,
                transform: 'perspective(2000px) rotateY(5deg)',
              }}
            >
              <div 
                className="relative w-[200px] xl:w-[240px]"
                style={{
                  borderRadius: '18px',
                  padding: '10px',
                  background: 'linear-gradient(160deg, #2c2c2e 0%, #1c1c1e 40%, #0e0e0e 100%)',
                  boxShadow: '0 40px 80px -20px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.04), inset 0 1px 0 rgba(255,255,255,0.06)',
                }}
              >
                {/* Camera dot */}
                <div className="absolute top-[4px] left-1/2 -translate-x-1/2 w-[5px] h-[5px] rounded-full" style={{ background: '#1a1a1e' }} />
                {/* Screen */}
                <div className="w-full aspect-[3/4] rounded-[8px] overflow-hidden relative" style={{ backgroundColor: '#0a0a0c' }}>
                  <img 
                    src="/devices/tablet.jpg" 
                    alt="Trading Terminal Tablet"
                    className="w-full h-full object-cover"
                  />
                  {/* Glass reflection */}
                  <div 
                    className="absolute inset-0 pointer-events-none"
                    style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, transparent 50%, transparent 100%)' }}
                  />
                </div>
              </div>
            </div>

            {/* === CENTER: Desktop Monitor (Hero) === */}
            <div className="relative z-10 flex flex-col items-center">
              {/* Ambient glow behind monitor */}
              <div 
                className="absolute -inset-10 pointer-events-none"
                style={{
                  background: 'radial-gradient(ellipse at center, rgba(26,90,150,0.06) 0%, transparent 60%)',
                  filter: 'blur(30px)',
                }}
              />
              {/* Monitor body */}
              <div 
                className="relative w-[340px] sm:w-[480px] lg:w-[600px] xl:w-[680px]"
                style={{
                  borderRadius: '12px 12px 0 0',
                  padding: '8px 8px 20px 8px',
                  background: 'linear-gradient(180deg, #2a2a2c 0%, #1e1e20 30%, #141416 100%)',
                  boxShadow: '0 60px 120px -30px rgba(0,0,0,0.9), 0 0 0 1px rgba(255,255,255,0.05), inset 0 1px 0 rgba(255,255,255,0.08)',
                }}
              >
                {/* Webcam dot */}
                <div className="absolute top-[3px] left-1/2 -translate-x-1/2 w-[4px] h-[4px] rounded-full" style={{ background: '#0a0a0c', boxShadow: 'inset 0 0 2px rgba(0,0,0,0.8)' }} />
                {/* Screen */}
                <div className="w-full aspect-[16/10] rounded-[4px] overflow-hidden relative" style={{ backgroundColor: '#0a0a0c' }}>
                  <img 
                    src="/devices/desktop.jpg" 
                    alt="Trading Terminal Desktop"
                    className="w-full h-full object-cover"
                  />
                  {/* Screen reflection overlay */}
                  <div 
                    className="absolute inset-0 pointer-events-none"
                    style={{ background: 'linear-gradient(140deg, rgba(255,255,255,0.02) 0%, transparent 40%, transparent 100%)' }}
                  />
                </div>
                {/* Brand logo under screen */}
                <div className="flex justify-center mt-2">
                  <div className="w-6 h-[2px] rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }} />
                </div>
              </div>
              {/* Monitor chin / stand neck */}
              <div 
                className="w-16 sm:w-20 h-12 relative"
                style={{ zIndex: 1 }}
              >
                {/* Neck */}
                <div 
                  className="absolute top-0 left-1/2 -translate-x-1/2 w-6 sm:w-8 h-full"
                  style={{ 
                    background: 'linear-gradient(90deg, #1a1a1c 0%, #252527 50%, #1a1a1c 100%)',
                    boxShadow: '0 0 0 1px rgba(255,255,255,0.03)',
                  }}
                />
              </div>
              {/* Monitor base */}
              <div 
                className="w-28 sm:w-36 h-[6px] rounded-[3px]"
                style={{ 
                  background: 'linear-gradient(90deg, #18181a 0%, #252527 30%, #2a2a2c 50%, #252527 70%, #18181a 100%)',
                  boxShadow: '0 4px 20px -4px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)',
                  marginTop: '-2px',
                }}
              />
            </div>

            {/* === RIGHT: MacBook Laptop === */}
            <div 
              className="absolute hidden md:block"
              style={{ 
                right: '3%', 
                bottom: '40px',
                zIndex: 3,
                transform: 'perspective(2000px) rotateY(-5deg)',
              }}
            >
              {/* Laptop lid */}
              <div 
                className="relative w-[200px] lg:w-[270px] xl:w-[310px]"
                style={{
                  borderRadius: '10px 10px 0 0',
                  padding: '6px 6px 12px 6px',
                  background: 'linear-gradient(180deg, #b8b8bc 0%, #a8a8ac 20%, #98989c 100%)',
                  boxShadow: '0 40px 80px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
                }}
              >
                {/* Webcam dot */}
                <div className="absolute top-[2px] left-1/2 -translate-x-1/2 w-[3px] h-[3px] rounded-full" style={{ background: '#7a7a7e' }} />
                {/* Inner black bezel */}
                <div 
                  className="rounded-[4px] overflow-hidden"
                  style={{ 
                    padding: '2px',
                    background: '#0e0e10',
                    boxShadow: 'inset 0 0 4px rgba(0,0,0,0.5)',
                  }}
                >
                  <div className="w-full aspect-[16/10] rounded-[2px] overflow-hidden relative" style={{ backgroundColor: '#0a0a0c' }}>
                    <img 
                      src="/devices/laptop.jpg" 
                      alt="Trading Terminal Laptop"
                      className="w-full h-full object-cover"
                    />
                    {/* Glass reflection */}
                    <div 
                      className="absolute inset-0 pointer-events-none"
                      style={{ background: 'linear-gradient(130deg, rgba(255,255,255,0.04) 0%, transparent 45%, transparent 100%)' }}
                    />
                  </div>
                </div>
              </div>
              {/* Laptop base / keyboard */}
              <div 
                className="w-[212px] lg:w-[286px] xl:w-[328px] h-[6px] -ml-[6px] lg:-ml-[8px] xl:-ml-[9px] rounded-b-[6px]"
                style={{ 
                  background: 'linear-gradient(180deg, #acacb0 0%, #8e8e92 50%, #78787c 100%)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.05)',
                }}
              >
                {/* Trackpad notch */}
                <div className="mx-auto w-12 lg:w-16 h-[2px] rounded-b-sm" style={{ background: '#6e6e72' }} />
              </div>
            </div>

            {/* === FRONT-RIGHT: iPhone === */}
            <div 
              className="absolute"
              style={{ 
                left: '62%',
                bottom: '15px',
                zIndex: 15,
              }}
            >
              <div 
                className="relative w-[72px] lg:w-[84px]"
                style={{
                  borderRadius: '16px',
                  padding: '6px 4px',
                  background: 'linear-gradient(160deg, #2c2c2e 0%, #1c1c1e 50%, #0c0c0e 100%)',
                  boxShadow: '0 30px 60px -15px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.06), inset 0 1px 0 rgba(255,255,255,0.08)',
                }}
              >
                {/* Dynamic Island */}
                <div className="absolute top-[6px] left-1/2 -translate-x-1/2 w-[24px] lg:w-[28px] h-[7px] lg:h-[8px] rounded-full" style={{ background: '#0a0a0c' }} />
                {/* Screen */}
                <div className="w-full aspect-[9/19.5] rounded-[10px] overflow-hidden relative" style={{ backgroundColor: '#0a0a0c' }}>
                  <img 
                    src="/devices/phone.jpg" 
                    alt="Trading Terminal Phone"
                    className="w-full h-full object-cover"
                  />
                  {/* Glass reflection */}
                  <div 
                    className="absolute inset-0 pointer-events-none"
                    style={{ background: 'linear-gradient(140deg, rgba(255,255,255,0.04) 0%, transparent 40%, transparent 100%)' }}
                  />
                </div>
                {/* Home indicator */}
                <div className="flex justify-center mt-1">
                  <div className="w-8 lg:w-10 h-[3px] rounded-full" style={{ background: 'rgba(255,255,255,0.12)' }} />
                </div>
              </div>
            </div>

          </div>
          
          {/* Platform badges */}
          <div className="flex flex-wrap items-center justify-center gap-3 mt-20">
            {['Windows', 'macOS', 'Linux', 'iOS', 'Android', 'Web Browser'].map((text, idx) => (
              <div 
                key={idx}
                className="px-5 py-2.5 rounded-full text-sm transition-colors"
                style={{ 
                  backgroundColor: 'rgba(255,255,255,0.03)',
                  color: THEME.textSecondary,
                  border: '1px solid rgba(255,255,255,0.05)',
                }}
                data-testid={`platform-badge-${text.toLowerCase().replace(' ', '-')}`}
              >
                {text}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PRICING ===== */}
      <section id="pricing" className="py-24" style={{ backgroundColor: THEME.bgCard }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm font-medium mb-3" style={{ color: THEME.accent }}>Pricing</p>
            <h2 className="text-3xl md:text-4xl font-semibold mb-4" style={{ color: THEME.text }}>
              Simple pricing
            </h2>
            <p className="text-base" style={{ color: THEME.textMuted }}>
              Start with a 14-day free trial. No credit card required.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {PRICING.map((plan, idx) => (
              <div
                key={idx}
                className="relative p-6 rounded-2xl"
                style={{ 
                  backgroundColor: plan.popular ? THEME.bgElevated : THEME.bg,
                }}
              >
                {plan.popular && (
                  <div 
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-medium"
                    style={{ backgroundColor: THEME.accent, color: '#fff' }}
                  >
                    Popular
                  </div>
                )}
                
                <div className="mb-6">
                  <h3 className="text-base font-medium mb-1" style={{ color: THEME.text }}>{plan.name}</h3>
                  <p className="text-sm mb-4" style={{ color: THEME.textMuted }}>{plan.desc}</p>
                  <div className="flex items-baseline">
                    {plan.price !== 'Custom' && <span className="text-sm" style={{ color: THEME.textMuted }}>$</span>}
                    <span className="text-3xl font-semibold" style={{ color: THEME.text }}>{plan.price}</span>
                    {plan.price !== 'Custom' && <span className="text-sm ml-1" style={{ color: THEME.textMuted }}>/mo</span>}
                  </div>
                </div>
                
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: THEME.textMuted }} />
                      <span className="text-sm" style={{ color: THEME.textSecondary }}>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button
                  onClick={scrollToInquiry}
                  className="w-full py-2.5 rounded-full text-sm font-medium transition-colors"
                  style={{ 
                    backgroundColor: plan.popular ? THEME.text : THEME.bgCard,
                    color: plan.popular ? THEME.bg : THEME.textSecondary,
                  }}
                  onMouseEnter={(e) => {
                    if (!plan.popular) e.target.style.backgroundColor = THEME.bgElevated;
                  }}
                  onMouseLeave={(e) => {
                    if (!plan.popular) e.target.style.backgroundColor = THEME.bgCard;
                  }}
                >
                  {plan.price === 'Custom' ? 'Contact sales' : 'Start trial'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== INQUIRY FORM ===== */}
      <section ref={inquiryRef} id="contact" className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left */}
            <div>
              <p className="text-sm font-medium mb-3" style={{ color: THEME.accent }}>Contact</p>
              <h2 className="text-3xl md:text-4xl font-semibold mb-6" style={{ color: THEME.text }}>
                Get started
              </h2>
              <p className="text-base mb-10 leading-relaxed" style={{ color: THEME.textMuted, maxWidth: '400px' }}>
                Tell us about your brokerage. Our team will reach out within 24 hours.
              </p>
              
              <div className="space-y-3">
                {[
                  { icon: Clock, text: 'Response within 24 hours' },
                  { icon: Globe, text: 'Support in 12 languages' },
                  { icon: Shield, text: 'Enterprise-grade security' },
                ].map((item, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center gap-4 p-4 rounded-xl"
                    style={{ backgroundColor: THEME.bgCard }}
                  >
                    <item.icon className="h-4 w-4" style={{ color: THEME.textMuted }} />
                    <span className="text-sm" style={{ color: THEME.textSecondary }}>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Right - Form */}
            <div 
              className="p-6 rounded-2xl"
              style={{ backgroundColor: THEME.bgCard }}
            >
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium mb-2" style={{ color: THEME.textMuted }}>
                      Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-colors"
                      style={{ 
                        backgroundColor: THEME.bgElevated, 
                        color: THEME.text,
                      }}
                      onFocus={(e) => e.target.style.backgroundColor = THEME.bgHover}
                      onBlur={(e) => e.target.style.backgroundColor = THEME.bgElevated}
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-2" style={{ color: THEME.textMuted }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-colors"
                      style={{ 
                        backgroundColor: THEME.bgElevated, 
                        color: THEME.text,
                      }}
                      onFocus={(e) => e.target.style.backgroundColor = THEME.bgHover}
                      onBlur={(e) => e.target.style.backgroundColor = THEME.bgElevated}
                      placeholder="john@company.com"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium mb-2" style={{ color: THEME.textMuted }}>
                      Company
                    </label>
                    <input
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-colors"
                      style={{ 
                        backgroundColor: THEME.bgElevated, 
                        color: THEME.text,
                      }}
                      onFocus={(e) => e.target.style.backgroundColor = THEME.bgHover}
                      onBlur={(e) => e.target.style.backgroundColor = THEME.bgElevated}
                      placeholder="Company name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-2" style={{ color: THEME.textMuted }}>
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-colors"
                      style={{ 
                        backgroundColor: THEME.bgElevated, 
                        color: THEME.text,
                      }}
                      onFocus={(e) => e.target.style.backgroundColor = THEME.bgHover}
                      onBlur={(e) => e.target.style.backgroundColor = THEME.bgElevated}
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs font-medium mb-2" style={{ color: THEME.textMuted }}>
                    Plan
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {['starter', 'professional', 'enterprise'].map((plan) => (
                      <button
                        key={plan}
                        type="button"
                        onClick={() => setFormData({...formData, plan})}
                        className="py-2.5 rounded-xl text-xs font-medium capitalize transition-colors"
                        style={{ 
                          backgroundColor: formData.plan === plan ? THEME.accent : THEME.bgElevated,
                          color: formData.plan === plan ? '#fff' : THEME.textMuted,
                        }}
                      >
                        {plan}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs font-medium mb-2" style={{ color: THEME.textMuted }}>
                    Message
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none resize-none transition-colors"
                    style={{ 
                      backgroundColor: THEME.bgElevated, 
                      color: THEME.text,
                    }}
                    onFocus={(e) => e.target.style.backgroundColor = THEME.bgHover}
                    onBlur={(e) => e.target.style.backgroundColor = THEME.bgElevated}
                    placeholder="Tell us about your requirements..."
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2"
                  style={{ 
                    backgroundColor: THEME.text, 
                    color: THEME.bg,
                    opacity: submitting ? 0.7 : 1
                  }}
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-gray-400 border-t-gray-800 rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Submit request'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="relative overflow-hidden" style={{ backgroundColor: THEME.bg }}>
        {/* Real black hole with accretion disk - dimmed grayscale */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/3 w-[1000px] h-[700px] pointer-events-none">
          <img 
            src="https://images.unsplash.com/photo-1768824264646-ea641cc71f5e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxibGFjayUyMGhvbGUlMjBzcGFjZSUyMGFzdHJvbm9teXxlbnwwfHx8fDE3NzU1MTgzNjN8MA&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover"
            style={{ 
              filter: 'grayscale(100%) brightness(0.3) contrast(1.2)',
              opacity: 0.5,
              maskImage: 'radial-gradient(ellipse 70% 60% at center, black 0%, transparent 70%)',
              WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at center, black 0%, transparent 70%)',
            }}
          />
        </div>
        
        {/* Main footer content */}
        <div className="relative max-w-6xl mx-auto px-6">
          {/* Top section - CTA */}
          <div className="py-20 text-center border-b" style={{ borderColor: THEME.bgElevated }}>
            <h2 className="text-3xl md:text-4xl font-semibold mb-4" style={{ color: THEME.text }}>
              Ready to transform your brokerage?
            </h2>
            <p className="text-base mb-8 max-w-lg mx-auto" style={{ color: THEME.textMuted }}>
              Join 150+ brokers who have already launched with Astra Terminal.
            </p>
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={scrollToInquiry}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium"
                style={{ backgroundColor: '#d4d4d8', color: THEME.bg }}
              >
                Start free trial
                <ArrowRight className="h-4 w-4" />
              </button>
              <a
                href="/trading"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm"
                style={{ backgroundColor: THEME.bgCard, color: THEME.textSecondary }}
              >
                View Live Chart
              </a>
            </div>
          </div>
          
          {/* Middle section - Links grid */}
          <div className="py-16 grid grid-cols-2 md:grid-cols-6 gap-8">
            {/* Brand column */}
            <div className="col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src="/assets/astra-logo-full.png" 
                  alt="AstraTerminal" 
                  className="h-9 object-contain"
                  style={{ width: 'auto' }}
                />
              </div>
              <p className="text-sm leading-relaxed mb-6" style={{ color: THEME.textMuted, maxWidth: '280px' }}>
                The complete white-label trading infrastructure for modern forex brokerages. Deploy in days, scale forever.
              </p>
              
              {/* Newsletter */}
              <div className="mb-6">
                <p className="text-xs font-medium mb-3" style={{ color: THEME.textSecondary }}>Subscribe to updates</p>
                <div className="flex gap-2">
                  <input
                    type="email"
                    placeholder="Email address"
                    className="flex-1 px-4 py-2.5 rounded-lg text-sm outline-none"
                    style={{ backgroundColor: THEME.bgCard, color: THEME.text }}
                  />
                  <button
                    className="px-4 py-2.5 rounded-lg text-sm font-medium"
                    style={{ backgroundColor: THEME.accent, color: '#fff' }}
                  >
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              {/* Download brochure */}
              <a
                href="/AstraTerminal-Brochure-v2.pdf"
                download
                data-testid="footer-download-brochure"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-sm transition-all hover:brightness-110 hover:-translate-y-0.5"
                style={{
                  backgroundColor: THEME.accent,
                  color: '#fff',
                  boxShadow: `0 10px 30px -8px ${THEME.accent}80`,
                }}
              >
                <Download className="h-4 w-4" />
                Download PDF brochure
              </a>
            </div>
            
            {/* Product */}
            <div>
              <h4 className="text-xs font-semibold mb-4 uppercase tracking-wider" style={{ color: THEME.textSecondary }}>Product</h4>
              <ul className="space-y-3">
                {['Trading Terminal', 'Admin Dashboard', 'Client Portal', 'Mobile Apps', 'API Access', 'Integrations'].map((link) => (
                  <li key={link}>
                    <a 
                      href="#"
                      className="text-sm transition-colors"
                      style={{ color: THEME.textMuted }}
                      onMouseEnter={(e) => e.target.style.color = THEME.textSecondary}
                      onMouseLeave={(e) => e.target.style.color = THEME.textMuted}
                    >
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Solutions */}
            <div>
              <h4 className="text-xs font-semibold mb-4 uppercase tracking-wider" style={{ color: THEME.textSecondary }}>Solutions</h4>
              <ul className="space-y-3">
                {['Forex Brokers', 'Crypto Exchanges', 'Prop Trading', 'Signal Providers', 'IB Networks', 'Hedge Funds'].map((link) => (
                  <li key={link}>
                    <a 
                      href="#"
                      className="text-sm transition-colors"
                      style={{ color: THEME.textMuted }}
                      onMouseEnter={(e) => e.target.style.color = THEME.textSecondary}
                      onMouseLeave={(e) => e.target.style.color = THEME.textMuted}
                    >
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Resources */}
            <div>
              <h4 className="text-xs font-semibold mb-4 uppercase tracking-wider" style={{ color: THEME.textSecondary }}>Resources</h4>
              <ul className="space-y-3">
                {[
                  ['Documentation', '/docs/api'],
                  ['API Reference', '/docs/api#embed'],
                  ['Quick start', '/docs/api#quickstart'],
                  ['HMAC signing', '/docs/api#hmac'],
                  ['Changelog', '/docs/api#changelog'],
                  ['Security', '/legal/security'],
                ].map(([label, to]) => (
                  <li key={label}>
                    <a
                      href={to}
                      className="text-sm transition-colors"
                      style={{ color: THEME.textMuted }}
                      onMouseEnter={(e) => e.target.style.color = THEME.textSecondary}
                      onMouseLeave={(e) => e.target.style.color = THEME.textMuted}
                    >
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Company */}
            <div>
              <h4 className="text-xs font-semibold mb-4 uppercase tracking-wider" style={{ color: THEME.textSecondary }}>Company</h4>
              <ul className="space-y-3">
                {['About Us', 'Careers', 'Press Kit', 'Contact', 'Partners', 'Affiliates'].map((link) => (
                  <li key={link}>
                    <a 
                      href="#"
                      className="text-sm transition-colors"
                      style={{ color: THEME.textMuted }}
                      onMouseEnter={(e) => e.target.style.color = THEME.textSecondary}
                      onMouseLeave={(e) => e.target.style.color = THEME.textMuted}
                    >
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          {/* Bottom bar */}
          <div className="py-6 flex flex-col md:flex-row items-center justify-between gap-4" style={{ borderTop: `1px solid ${THEME.bgElevated}` }}>
            <div className="flex items-center gap-6">
              <p className="text-xs" style={{ color: THEME.textDim }}>
                © {new Date().getFullYear()} Astra Terminal. All rights reserved.
              </p>
            </div>
            <div className="flex items-center gap-6">
              {[
                ['Privacy Policy', '/legal/privacy'],
                ['Terms of Service', '/legal/terms'],
                ['Cookie Policy', '/legal/cookies'],
                ['Security', '/legal/security'],
              ].map(([label, to]) => (
                <a 
                  key={label}
                  href={to}
                  className="text-xs transition-colors"
                  style={{ color: THEME.textDim }}
                  onMouseEnter={(e) => e.target.style.color = THEME.textMuted}
                  onMouseLeave={(e) => e.target.style.color = THEME.textDim}
                >
                  {label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>
      <FloatingChatButtons />
    </div>
  );
}
